import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import { addLineCollection } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { QuoteModel } from "@insite/client-framework/Types/ApiModels";
import ProductSelectorVariantModal from "@insite/content-library/Components/ProductSelectorVariantModal";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen: boolean;
    quote: QuoteModel;
    onSuccess: () => void;
    onClose: () => void;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;

    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        productSettings: getSettingsCollection(state).productSettings,
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface AddQuoteLineModalStyles {
    modal?: ModalPresentationProps;
    form?: InjectableCss;
    container?: GridContainerProps;
    inputsContainer?: GridContainerProps;
    commonGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    unitOfMeasureGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    errorGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    qtyTextField?: TextFieldPresentationProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    notesTextField?: TextFieldPresentationProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const addQuoteLineModalStyles: AddQuoteLineModalStyles = {
    modal: {
        cssOverrides: {
            modalBody: css`
                overflow: visible;
            `,
        },
    },
    container: { gap: 15 },
    commonGridItem: { width: 12 },
    titleGridItem: { width: 12 },
    searchGridItem: { width: [12, 12, 6, 6, 6] },
    qtyGridItem: { width: [6, 6, 3, 3, 3] },
    unitOfMeasureGridItem: { width: [6, 6, 3, 3, 3] },
    notesGridItem: { width: 12 },
    errorGridItem: {
        width: 12,
        css: css`
            margin-top: 20px;
            justify-content: center;
            color: red;
        `,
    },
    buttonsGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: 12,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 1rem;
        `,
    },
    submitButton: {
        css: css`
            margin-left: 1rem;
        `,
    },
    titleText: {
        variant: "h4",
    },
};

const styles = addQuoteLineModalStyles;

const StyledForm = getStyledWrapper("form");

const ENTER_KEY = 13;

const AddQuoteLineModal = ({
    isOpen,
    quote,
    onClose,
    onSuccess,
    toaster,
    isSearching,
    searchResults,
    selectedProductInfo,
    product,
    errorType,
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
    productSettings,
}: Props) => {
    const [quantity, setQuantity] = useState("1");
    const [notes, setNotes] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedProductId, setSelectedProductId] = useState("");
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [unitOfMeasure, setUnitOfMeasureState] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: `${product.erpNumber ? product.erpNumber : ""} - ${product.title}`,
            optionValue: product.id || undefined,
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
        setUnitOfMeasureState(selectedProductInfo ? selectedProductInfo.unitOfMeasure : "");
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    const quantityChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuantity(event.target.value);
    };

    const notesChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotes(event.target.value);
    };

    const validateForm = () => {
        if (!selectedProductId) {
            setErrorMessage(translate("Please select a product"));
            return false;
        }

        const qty = parseFloat(quantity);
        if (!qty || qty <= 0) {
            setErrorMessage(translate("Quantity must be greater than 0"));
            return false;
        }

        return true;
    };

    const formSubmitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validateForm() || !product) {
            return false;
        }

        setIsSubmitting(true);

        try {
            await addLineCollection({
                cartId: quote.id,
                cartLineCollection: {
                    cartLines: [
                        {
                            productId: selectedProductId,
                            qtyOrdered: parseFloat(quantity),
                            unitOfMeasure: unitOfMeasure || product.unitOfMeasures?.[0]?.unitOfMeasure || "EA",
                            notes: notes || "",
                        } as any,
                    ],
                } as any,
            });

            toaster.addToast({
                body: translate("Product added to quote successfully"),
                messageType: "success",
            });

            onSuccess();
            resetState();
            closeModalHandler();
        } catch (error) {
            toaster.addToast({
                body: error?.errorJson?.message || translate("Failed to add product to quote"),
                messageType: "danger",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetState = () => {
        setQuantity("1");
        setNotes("");
        setOptions([]);
        setSelectedProductId("");
        setErrorMessage("");
        setUnitOfMeasureState("");
        setSearchTerm("");
    };

    const closeModalHandler = () => {
        onClose();
        resetState();
    };

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation: true,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation: true,
                });
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            clearProduct();
        }

        debouncedSearchProducts(event.target.value);
    };

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
        setUnitOfMeasureState(unitOfMeasure);
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (!selectedProductInfo && searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({ searchTerm, validateProduct: true, skipInventoryValidation: true });
            }
        }
    };

    const showUnitOfMeasure =
        productSettings?.alternateUnitsOfMeasure &&
        product?.unitOfMeasures &&
        product.unitOfMeasures.length > 1 &&
        product.unitOfMeasures.some(uom => uom.unitOfMeasure);

    return (
        <Modal
            {...styles.modal}
            handleClose={closeModalHandler}
            headline={translate("Add Line Item(s)")}
            isOpen={isOpen}
            size={700}
        >
            <StyledForm {...styles.form} noValidate onSubmit={formSubmitHandler}>
                <GridContainer {...styles.container}>
                    <GridItem {...styles.titleGridItem}>
                        <Typography as="h4" {...styles.titleText}>
                            {translate("Product Information")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.commonGridItem}>
                        <GridContainer {...styles.inputsContainer}>
                            <GridItem {...styles.searchGridItem}>
                                <DynamicDropdown
                                    {...styles.searchDynamicDropdown}
                                    label={translate("Product")}
                                    onSelectionChange={onSelectionChangeHandler}
                                    onInputChange={onInputChanged}
                                    filterOption={() => true}
                                    selected={selectedProductId}
                                    isLoading={isSearching}
                                    options={options}
                                    error={errorMessage}
                                    required
                                    onKeyPress={onKeyPress}
                                    data-test-selector="add-quote-line-product-dropdown"
                                />
                                <ProductSelectorVariantModal skipInventoryValidation={true} />
                            </GridItem>
                            <GridItem {...styles.qtyGridItem}>
                                <TextField
                                    {...styles.qtyTextField}
                                    type="number"
                                    label={translate("Quantity")}
                                    value={quantity}
                                    onChange={quantityChangeHandler}
                                    required
                                    min={1}
                                />
                            </GridItem>
                            {showUnitOfMeasure && product && selectedProductInfo && (
                                <GridItem {...styles.unitOfMeasureGridItem}>
                                    <ProductContext.Provider
                                        value={{
                                            product,
                                            productInfo: { ...selectedProductInfo, unitOfMeasure },
                                            onUnitOfMeasureChanged: uomChangeHandler,
                                        }}
                                    >
                                        <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                                    </ProductContext.Provider>
                                </GridItem>
                            )}
                            <GridItem {...styles.notesGridItem}>
                                <TextField
                                    {...styles.notesTextField}
                                    label={translate("Notes")}
                                    value={notes}
                                    onChange={notesChangeHandler}
                                    placeholder={translate("Add any notes for this line item")}
                                />
                            </GridItem>
                            <GridItem {...styles.errorGridItem}>{errorMessage}</GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem}>
                        <Button {...styles.cancelButton} onClick={closeModalHandler} disabled={isSubmitting}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.submitButton}
                            type="submit"
                            disabled={!selectedProductId || !!errorMessage || isSubmitting}
                        >
                            {isSubmitting ? translate("Adding...") : translate("Add to Quote")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(AddQuoteLineModal));
